/**
 * Grenade sound designs for Counter Pump.
 *
 * 9 grenade-related sounds with realistic multi-layered synthesis:
 * - HE Explode: massive bass boom + shrapnel + shockwave
 * - Smoke Pop: compressed air release + canister clatter
 * - Smoke Hiss: continuous white noise with slow modulation (long)
 * - Flash Explode: bright concussive pop
 * - Flash Tinnitus: high-frequency ear ringing (long)
 * - Fire Ignite: whoosh + liquid splash + flame burst
 * - Fire Crackle: randomized crackle pattern (long)
 * - Grenade Bounce: hard impact + metallic ring
 * - Grenade Pin: metal click + spring release
 */

import type { SoundDesign, SoundLayer } from './sound-designer';

const SR = 44100;

function osc(
  freq: number,
  waveform: OscillatorType,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'oscillator', waveform, frequency: freq,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function noise(
  noiseType: 'white' | 'pink' | 'brown',
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'noise', noiseType, attack, decay, sustain, release, gain, ...opts,
  };
}

function fnoise(
  noiseType: 'white' | 'pink' | 'brown',
  filterType: BiquadFilterType,
  filterFreq: number,
  filterQ: number,
  gain: number,
  attack: number,
  decay: number,
  sustain: number,
  release: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'filtered-noise', noiseType, filterType, filterFreq, filterQ,
    attack, decay, sustain, release, gain, ...opts,
  };
}

function impulse(
  gain: number,
  duration: number,
  opts: Partial<SoundLayer> = {},
): SoundLayer {
  return {
    type: 'impulse', attack: 0, decay: 0, sustain: 1, release: 0,
    gain, duration, ...opts,
  };
}

export function getGrenadeSoundDesigns(): Map<string, SoundDesign> {
  const designs = new Map<string, SoundDesign>();

  // ─── HE EXPLODE ───────────────────────────────────────────────────
  designs.set('he_explode', {
    name: 'he_explode',
    duration: 0.8,
    sampleRate: SR,
    layers: [
      impulse(0.95, 0.01),
      osc(30, 'sine', 0.6, 0.003, 0.1, 0.08, 0.35),
      osc(80, 'sine', 0.45, 0.002, 0.06, 0.1, 0.25),
      osc(60, 'sawtooth', 0.25, 0.003, 0.05, 0.06, 0.2, { endFrequency: 25 }),
      noise('white', 0.4, 0.001, 0.04, 0.1, 0.25),
      fnoise('pink', 'bandpass', 1500, 1, 0.3, 0.002, 0.05, 0.08, 0.3),
      noise('brown', 0.2, 0.01, 0.06, 0.06, 0.35),
      fnoise('white', 'highpass', 4000, 2, 0.12, 0.001, 0.01, 0.03, 0.05,
        { startTime: 0.0, duration: 0.1 }),
    ],
    compression: { threshold: 0.3, ratio: 5, attack: 0.001, release: 0.03 },
    distortion: { amount: 1.5, oversample: 'none' },
  });

  // ─── SMOKE POP ────────────────────────────────────────────────────
  designs.set('smoke_pop', {
    name: 'smoke_pop',
    duration: 0.3,
    sampleRate: SR,
    layers: [
      impulse(0.6, 0.005),
      fnoise('white', 'bandpass', 3000, 2, 0.35, 0.002, 0.03, 0.1, 0.12),
      osc(800, 'sine', 0.15, 0.001, 0.01, 0.05, 0.04, { endFrequency: 300 }),
      fnoise('pink', 'highpass', 2000, 1.5, 0.15, 0.001, 0.02, 0.06, 0.08),
      osc(200, 'triangle', 0.12, 0.002, 0.015, 0.04, 0.05),
    ],
    compression: { threshold: 0.45, ratio: 3, attack: 0.001, release: 0.015 },
  });

  // ─── SMOKE HISS ───────────────────────────────────────────────────
  designs.set('smoke_hiss', {
    name: 'smoke_hiss',
    duration: 1.5,
    sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 4000, 1, 0.12, 0.15, 0.1, 0.7, 0.3),
      fnoise('pink', 'lowpass', 2000, 0.7, 0.06, 0.2, 0.15, 0.6, 0.3),
      osc(3000, 'sine', 0.02, 0.2, 0.1, 0.5, 0.25),
    ],
  });

  // ─── FLASH EXPLODE ────────────────────────────────────────────────
  designs.set('flash_explode', {
    name: 'flash_explode',
    duration: 0.35,
    sampleRate: SR,
    layers: [
      impulse(0.9, 0.006),
      osc(400, 'square', 0.4, 0.001, 0.01, 0.05, 0.04),
      osc(200, 'sawtooth', 0.3, 0.001, 0.015, 0.06, 0.06),
      noise('white', 0.35, 0.001, 0.02, 0.08, 0.12),
      osc(800, 'sine', 0.2, 0.001, 0.005, 0.03, 0.03, { endFrequency: 300 }),
      fnoise('pink', 'bandpass', 2000, 1.5, 0.15, 0.002, 0.02, 0.05, 0.1),
    ],
    compression: { threshold: 0.35, ratio: 4, attack: 0.001, release: 0.02 },
    distortion: { amount: 1.3, oversample: 'none' },
  });

  // ─── FLASH TINNITUS ───────────────────────────────────────────────
  designs.set('flash_tinnitus', {
    name: 'flash_tinnitus',
    duration: 3.0,
    sampleRate: SR,
    layers: [
      osc(4000, 'sine', 0.3, 0.02, 0.1, 0.6, 1.0),
      osc(4200, 'sine', 0.12, 0.03, 0.15, 0.5, 1.0),
      osc(3800, 'sine', 0.08, 0.04, 0.12, 0.45, 1.0),
      osc(6000, 'sine', 0.04, 0.05, 0.1, 0.3, 1.2),
    ],
  });

  // ─── FIRE IGNITE ──────────────────────────────────────────────────
  designs.set('fire_ignite', {
    name: 'fire_ignite',
    duration: 0.5,
    sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 3000, 1.5, 0.35, 0.01, 0.05, 0.12, 0.15),
      osc(200, 'sawtooth', 0.2, 0.01, 0.03, 0.08, 0.12, { endFrequency: 80 }),
      noise('pink', 0.25, 0.02, 0.05, 0.1, 0.15),
      fnoise('brown', 'lowpass', 1200, 1, 0.15, 0.03, 0.04, 0.06, 0.12),
      fnoise('white', 'highpass', 5000, 2, 0.08, 0.005, 0.02, 0.04, 0.06,
        { startTime: 0.02, duration: 0.15 }),
    ],
    compression: { threshold: 0.4, ratio: 3.5, attack: 0.002, release: 0.02 },
  });

  // ─── FIRE CRACKLE ─────────────────────────────────────────────────
  designs.set('fire_crackle', {
    name: 'fire_crackle',
    duration: 5.0,
    sampleRate: SR,
    layers: [
      fnoise('white', 'bandpass', 2000, 3, 0.12, 0.02, 0.05, 0.8, 0.5),
      fnoise('brown', 'lowpass', 400, 0.7, 0.06, 0.05, 0.1, 0.75, 0.5),
      osc(120, 'sawtooth', 0.04, 0.1, 0.15, 0.6, 0.5),
      fnoise('white', 'highpass', 4000, 2, 0.05, 0.01, 0.03, 0.7, 0.5),
      osc(80, 'sine', 0.03, 0.05, 0.1, 0.5, 0.4),
    ],
  });

  // ─── GRENADE BOUNCE ───────────────────────────────────────────────
  designs.set('grenade_bounce', {
    name: 'grenade_bounce',
    duration: 0.1,
    sampleRate: SR,
    layers: [
      impulse(0.65, 0.004),
      osc(300, 'square', 0.25, 0.001, 0.015, 0.04, 0.03),
      osc(600, 'triangle', 0.12, 0.001, 0.01, 0.03, 0.025),
      osc(1800, 'sine', 0.06, 0.001, 0.005, 0.02, 0.015),
      fnoise('white', 'highpass', 3000, 2, 0.08, 0.001, 0.008, 0.03, 0.02),
    ],
    compression: { threshold: 0.5, ratio: 3, attack: 0.001, release: 0.008 },
  });

  // ─── GRENADE PIN ──────────────────────────────────────────────────
  designs.set('grenade_pin', {
    name: 'grenade_pin',
    duration: 0.05,
    sampleRate: SR,
    layers: [
      impulse(0.5, 0.003),
      osc(1200, 'sine', 0.2, 0.001, 0.008, 0.03, 0.01),
      osc(2400, 'sine', 0.08, 0.001, 0.005, 0.02, 0.008),
      fnoise('white', 'highpass', 4000, 2, 0.06, 0.001, 0.005, 0.02, 0.01),
    ],
    compression: { threshold: 0.5, ratio: 2.5, attack: 0.001, release: 0.006 },
  });

  return designs;
}
